<template>
    <v-card class="theme-card pa-3 rounded-lg h-full d-flex flex-column" :class="`state-${classification}`" outlined role="article">
        <div class="theme-head d-flex align-center mb-2">
            <span class="theme-index primary--text mr-2">{{ formattedIndex }}</span>
            <span v-if="suggestion.keyword" class="theme-keyword-chip">{{ suggestion.keyword }}</span>
            <v-spacer />
            <span v-if="classification === 'relevant'" class="state-badge relevant">
                <v-icon size="13" class="mr-1">mdi-check</v-icon>Vou usar
            </span>
            <span v-else-if="classification === 'discarded'" class="state-badge discarded">
                <v-icon size="13" class="mr-1">mdi-close</v-icon>Não quero
            </span>
        </div>

        <p class="theme-title stardust--text font-weight-medium ma-0 mb-1">{{ suggestion.title || suggestion.theme }}</p>
        <p v-if="suggestion.theme && suggestion.theme !== suggestion.title" class="theme-subline mercury--text ma-0 mb-1">{{ suggestion.theme }}</p>
        <p v-if="suggestion.meta_description" class="theme-meta mercury--text ma-0">{{ suggestion.meta_description }}</p>

        <v-spacer />

        <div class="theme-actions mt-3">
            <div v-if="classification === 'unclassified'" class="actions-row">
                <button type="button" class="act-btn relevant-btn" @click="$emit('classify', 'relevant')">
                    <v-icon size="16" class="mr-1">mdi-check-circle-outline</v-icon>Quero usar
                </button>
                <button type="button" class="act-btn discard-btn" @click="$emit('classify', 'discarded')">
                    <v-icon size="16" class="mr-1">mdi-close-circle-outline</v-icon>Não quero
                </button>
            </div>

            <div v-else-if="classification === 'relevant'" class="actions-row">
                <v-btn color="primary" small depressed class="gen-btn flex-grow-1" @click="$emit('generate')">
                    <v-icon left size="16">mdi-creation</v-icon>Criar artigo
                </v-btn>
                <v-btn icon small class="icon-mini" title="Não quero este tema" @click="$emit('classify', 'discarded')">
                    <v-icon size="18">mdi-close</v-icon>
                </v-btn>
            </div>

            <div v-else class="actions-row">
                <button type="button" class="act-btn restore-btn" @click="$emit('classify', 'unclassified')">
                    <v-icon size="16" class="mr-1">mdi-restore</v-icon>Trazer de volta
                </button>
            </div>
        </div>
    </v-card>
</template>

<script>
export default {
    name: 'ArtigosSuggestionCard',
    props: {
        index: { type: Number, required: true },
        suggestion: { type: Object, required: true },
    },
    computed: {
        classification() {
            return this.suggestion.classification || 'unclassified'
        },
        formattedIndex() {
            return String(this.index).padStart(2, '0')
        },
    },
}
</script>

<style scoped>
.theme-card {
    position: relative;
    border: 1px solid rgba(112, 105, 161, 0.18) !important;
    transition: transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease, opacity 220ms ease;
    box-shadow: 0 1px 2px rgba(15, 21, 28, 0.04);
    will-change: transform, box-shadow;
}

.theme-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 28px rgba(15, 21, 28, 0.08);
}

/* Estados */
.state-unclassified {
    border-color: rgba(112, 105, 161, 0.22) !important;
}

.state-relevant {
    border-color: rgba(112, 42, 225, 0.45) !important;
    background: rgba(112, 42, 225, 0.03);
}

.state-discarded {
    opacity: 0.6;
    background: rgba(15, 21, 28, 0.02);
}

.state-discarded .theme-title {
    text-decoration: line-through;
    text-decoration-color: rgba(91, 91, 100, 0.5);
}

.theme-head {
    min-height: 22px;
}

.theme-index {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.1em;
    font-variant-numeric: tabular-nums;
}

.theme-keyword-chip {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 999px;
    background: rgba(0, 115, 230, 0.1);
    color: var(--v-primary-base);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.02em;
    line-height: 1.4;
    max-width: 60%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.state-badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    line-height: 1.4;
    white-space: nowrap;
}

.state-badge.relevant {
    background: rgba(112, 42, 225, 0.12);
    color: var(--color-primary, #702ae1);
}

.state-badge.discarded {
    background: rgba(180, 19, 64, 0.1);
    color: var(--color-error, #b41340);
}

.theme-title {
    font-size: 14.5px;
    line-height: 1.35;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.theme-subline {
    font-size: 12px;
    line-height: 1.35;
    font-style: italic;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.theme-meta {
    font-size: 12px;
    line-height: 1.45;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

/* Ações */
.actions-row {
    display: flex;
    align-items: center;
    gap: 8px;
}

.act-btn {
    flex: 1 1 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 7px 10px;
    border-radius: var(--radius-xs, 8px);
    border: 1px solid var(--glass-panel-border, rgba(112, 105, 161, 0.22));
    background: var(--glass-soft-bg, rgba(255, 255, 255, 0.4));
    color: var(--color-on-surface-variant, #5b5b64);
    font-size: 12.5px;
    font-weight: 600;
    line-height: 1.2;
    cursor: pointer;
    transition: border-color 150ms ease, background 150ms ease, color 150ms ease;
}

.relevant-btn:hover {
    border-color: var(--color-primary, #702ae1);
    background: rgba(112, 42, 225, 0.08);
    color: var(--color-primary, #702ae1);
}

.discard-btn:hover {
    border-color: var(--color-error, #b41340);
    background: rgba(180, 19, 64, 0.06);
    color: var(--color-error, #b41340);
}

.restore-btn:hover {
    border-color: var(--color-primary, #702ae1);
    background: rgba(112, 42, 225, 0.06);
    color: var(--color-primary, #702ae1);
}

.gen-btn {
    text-transform: none;
    letter-spacing: 0;
    font-weight: 700;
    border-radius: var(--radius-xs, 8px);
}

.icon-mini {
    color: var(--color-on-surface-variant, #5b5b64);
}
</style>
